import { createSlice, PayloadAction } from '@reduxjs/toolkit';
// import { signIn, signOut } from './thunks';
import { AuthState, RefreshToken, User } from './types';
import { decodeToken, JWT_DEcode } from '@wajeez-web/core/utils';
import {
  getLocalAccessToken,
  getLocalRefreshToken,
  getUser,
} from './TokenHelper/TokenStorageManager';
import {
  updateUserNameAsync,
  changeEmailAsync,
  changePasswordAsync,
  requestEmailVerificationAsync,
  validateEmailAsync,
  requestPasswordResetAsync,
  signUpUserAsync,
  signUpUserAffiliateAsync,
  signInB2bAsync,
  checkLoginOptionAsync,
  signInB2bByTokenAsync,
  requestWhatsappOtpAsync,
} from './thunks';

export const name = 'auth';

const decodedToken: JWT_DEcode | null = getLocalAccessToken()
  ? decodeToken(getLocalAccessToken() || '')
  : null;

const emptyState: AuthState = {
  user: {
    id: '',
    email: '',
    name: '',
    isVerified: false,
    picture: '',
    autoPlayDisabled: false,
    nextTitleDisabled: false,
  },
  token: null,
  initURL: '',
  permission: null,
  role: null,
  tokenExpiry: 0,
  refreshToken: null,
  headers: {
    Accept: 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
    Expires: '0',
    'content-language': 'ar',

    // 'accept': 'text/plain',
  },
  status: 'idle',
  isAccountDeleted: false,
  validateEmailStatus: 'idle',
  requestPasswordResetStatus: 'idle',
  signedUpUser: {
    data: undefined,
    status: 'idle',
  },
  signedUpUserAffiliate: {
    data: undefined,
    status: 'idle',
  },
  b2bLogin: {
    data: null,
    status: 'idle',
  },
  checkLoginOption: {
    data: null,
    status: 'idle',
  },
  changePassword: {
    status: 'idle',
  },
  b2bLoginByToken: {
    data: null,
    status: 'idle',
  },
  requestWhatsappOtp: {
    data: null,
    status: 'idle',
  },
};

const initialState: AuthState = {
  user: getUser(),
  token: getLocalAccessToken(),
  initURL: '',
  permission: decodedToken ? decodedToken.Permission : null,
  role: decodedToken ? decodedToken.Role : null,
  tokenExpiry: decodedToken?.exp ?? 0,
  refreshToken: getLocalRefreshToken(),
  headers: {
    Accept: 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
    Expires: '0',
    // 'accept': 'text/plain',
    Authorization: 'Bearer ' + (getLocalAccessToken() || ''),
  },
  status: 'idle',
  isAccountDeleted: false,
  validateEmailStatus: 'idle',
  requestPasswordResetStatus: 'idle',
  signedUpUser: {
    data: undefined,
    status: 'idle',
  },
  signedUpUserAffiliate: {
    data: undefined,
    status: 'idle',
  },
  b2bLogin: {
    data: null,
    status: 'idle',
  },
  checkLoginOption: {
    data: null,
    status: 'idle',
  },
  changePassword: {
    status: 'idle',
  },
  b2bLoginByToken: {
    data: null,
    status: 'idle',
  },
  requestWhatsappOtp: {
    data: null,
    status: 'idle',
  },
};

export const slice = createSlice({
  name: name,
  initialState,
  reducers: {
    resetAuth: () => {
      return emptyState;
    },
    resetAuthWithAction: (state, action) => {
      return { ...emptyState, ...action.payload };
    },
    setUser: (
      state,
      action: PayloadAction<{
        token: string;
        refreshToken: string;
        user: User;
      }>
    ) => {
      const decodedToken = decodeToken(action.payload.token);
      console.log(decodedToken);
      return {
        ...state,
        user: action.payload.user,
        refreshToken: action.payload.refreshToken,
        headers: {
          ...state.headers,
          Authorization: 'Bearer ' + action.payload.token,
        },
        token: action.payload.token,
        permission: decodedToken.Permission,
        role: decodedToken.Role,
        tokenExpiry: decodedToken.exp ?? 0,
      };
    },
    setUserDetails: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    initUrl: (state, action: PayloadAction<string | null>) => {
      state.initURL = action.payload;
    },
    setHeaders: (state, action: PayloadAction<any>) => {
      state.headers = action.payload;
    },
    setToken: (state, action: PayloadAction<string>) => {
      const decodedToken = decodeToken(action.payload);

      state.token = action.payload;
      state.tokenExpiry = decodedToken.exp ?? 0;
    },
    setTokenWithUser: (state, action: PayloadAction<string>) => {
      const decodedToken = decodeToken(action.payload);

      const userData = decodedToken.UD
        ? JSON.parse(decodedToken.UD)
        : undefined;

      state.token = action.payload;
      state.tokenExpiry = decodedToken.exp ?? 0;
      state.user = {
        ...state.user,
        ...(userData ?? {}),
      };
    },
    setRefreshToken: (state, action: PayloadAction<RefreshToken>) => {
      const decodedToken = decodeToken(action.payload.token);

      return {
        ...state,
        refreshToken: action.payload.refreshToken,
        token: action.payload.token,
        tokenExpiry: decodedToken.exp ?? 0,
        headers: {
          ...state.headers,
          Authorization: 'Bearer ' + action.payload.token,
        },
        permission: decodedToken.Permission,
      };
    },
    setAutoplayDisabled: (state, action: PayloadAction<boolean>) => {
      state.user.autoPlayDisabled = action.payload;
    },
    setNextTitleDisabled: (state, action: PayloadAction<boolean>) => {
      state.user.nextTitleDisabled = action.payload;
    },
    clearCheckLoginOption: (state) => {
      state.checkLoginOption = {
        data: null,
        status: 'idle',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateUserNameAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateUserNameAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.user.name = action.payload.name;
      })
      .addCase(updateUserNameAsync.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(changeEmailAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(changeEmailAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.user.email = action.payload;
      })
      .addCase(changeEmailAsync.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(changePasswordAsync.pending, (state) => {
        state.status = 'loading';

        state.changePassword.status = 'loading';
      })
      .addCase(changePasswordAsync.fulfilled, (state) => {
        state.status = 'idle';

        state.changePassword.status = 'idle';
      })
      .addCase(changePasswordAsync.rejected, (state) => {
        state.status = 'failed';

        state.changePassword.status = 'failed';
      })
      .addCase(requestEmailVerificationAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(requestEmailVerificationAsync.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(requestEmailVerificationAsync.rejected, (state, action) => {
        const actionPayload: any = action.payload;

        if (actionPayload.message !== 'canceled') {
          state.status = 'failed';
        }
      })
      .addCase(validateEmailAsync.pending, (state) => {
        state.validateEmailStatus = 'loading';
      })
      .addCase(validateEmailAsync.fulfilled, (state) => {
        state.user.isVerified = true;
        state.validateEmailStatus = 'idle';
      })
      .addCase(validateEmailAsync.rejected, (state) => {
        state.validateEmailStatus = 'failed';
      })
      .addCase(requestPasswordResetAsync.pending, (state) => {
        state.requestPasswordResetStatus = 'loading';
      })
      .addCase(requestPasswordResetAsync.fulfilled, (state) => {
        state.requestPasswordResetStatus = 'idle';
      })
      .addCase(requestPasswordResetAsync.rejected, (state) => {
        state.requestPasswordResetStatus = 'failed';
      })
      .addCase(signUpUserAsync.pending, (state) => {
        state.signedUpUser.status = 'loading';
      })
      .addCase(signUpUserAsync.fulfilled, (state, action) => {
        state.signedUpUser = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(signUpUserAsync.rejected, (state) => {
        state.signedUpUser.status = 'failed';
      })
      .addCase(signUpUserAffiliateAsync.pending, (state) => {
        state.signedUpUserAffiliate.status = 'loading';
      })
      .addCase(signUpUserAffiliateAsync.fulfilled, (state, action) => {
        state.signedUpUserAffiliate = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(signUpUserAffiliateAsync.rejected, (state) => {
        state.signedUpUserAffiliate.status = 'failed';
      })
      .addCase(signInB2bAsync.pending, (state) => {
        state.b2bLogin.status = 'loading';
      })
      .addCase(signInB2bAsync.fulfilled, (state, action) => {
        state.b2bLogin = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(signInB2bAsync.rejected, (state) => {
        state.b2bLogin.status = 'failed';
      })
      .addCase(signInB2bByTokenAsync.pending, (state) => {
        state.b2bLoginByToken.status = 'loading';
      })
      .addCase(signInB2bByTokenAsync.fulfilled, (state, action) => {
        state.b2bLoginByToken = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(signInB2bByTokenAsync.rejected, (state) => {
        state.b2bLoginByToken.status = 'failed';
      })
      .addCase(checkLoginOptionAsync.pending, (state) => {
        state.checkLoginOption.status = 'loading';
      })
      .addCase(checkLoginOptionAsync.fulfilled, (state, action) => {
        state.checkLoginOption = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(checkLoginOptionAsync.rejected, (state) => {
        state.checkLoginOption.status = 'failed';
      })
      .addCase(requestWhatsappOtpAsync.pending, (state) => {
        state.requestWhatsappOtp.status = 'loading';
      })
      .addCase(requestWhatsappOtpAsync.fulfilled, (state, action) => {
        state.requestWhatsappOtp = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(requestWhatsappOtpAsync.rejected, (state) => {
        state.requestWhatsappOtp.status = 'failed';
      });
  },
});

export const actions = slice.actions;

export default slice.reducer;
